<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_posting_applications')"
                        :isColumns="true"
                        @filter-div-status="datatable.filterStatus = $event">
                    <template v-slot:columns>
                        <div class="mb-1" v-for="(column,key) in datatable.columns">
                            <b-form-checkbox
                                :id="'checkbox-'+key"
                                :name="'checkbox-'+key"
                                :value="false"
                                :unchecked-value="true"
                                v-model="column.hidden"
                                v-if="column.field!='buttons'"
                            >
                                {{ column.label }}
                            </b-form-checkbox>
                        </div>
                    </template>
                </Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_posting_applications')"
                              :isColumns="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <b-button variant="outline-secondary" class="mb-3" @click="$router.push('/coop/ads')">
                <i class="ri-arrow-left-line"></i> {{ $t('back') }}
            </b-button>

            <div class="mb-3" v-if="ad">
                <div><strong>{{ $t('coop_company') }}:</strong> {{ ad.company_name }}</div>
                <div><strong>{{ $t('coop_position') }}:</strong> {{ ad.position }}</div>
            </div>

            <datatable-filter v-show="datatable.filterStatus"
                              @filter="filter"
                              @filterClear="filterClear"
                              :export-excel="true"
                              @exportExcel="$refs.datatable.excel()">
                <b-row>
                    <b-col sm="12">
                        <fieldset class="border p-2 mb-2 rounded">
                            <legend class="w-auto px-2 small font-weight-bold">{{ $t('coop_gpa_info') }}</legend>
                            <b-row>
                                <b-col sm="6" md="3">
                                    <b-form-group :label="$t('coop_min')">
                                        <b-form-input v-model="datatable.queryParams.filter.gno_min" type="number" step="0.01"/>
                                    </b-form-group>
                                </b-col>
                                <b-col sm="6" md="3">
                                    <b-form-group :label="$t('coop_max')">
                                        <b-form-input v-model="datatable.queryParams.filter.gno_max" type="number" step="0.01"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </fieldset>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('faculty')">
                            <faculty-selectbox v-model="datatable.queryParams.filter.faculty_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('department')">
                            <department-selectbox :faculty_code="datatable.queryParams.filter.faculty_code"
                                                   v-model="datatable.queryParams.filter.department_code" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number"/>
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('class')">
                            <classes-selectbox v-model="datatable.queryParams.filter.class" />
                        </b-form-group>
                    </b-col>
                    <b-col sm="6" md="3">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="datatable.queryParams.filter.status" :options="statusFilterOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       :select-options="true"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       @on-selected-rows-change="onSelectedRowsChange"
                       v-show="datatable.showTable"
            />

            <div class="d-flex mt-2" v-if="datatable.showTable">
                <b-button variant="outline-primary" class="mr-2" @click="downloadSelectedCvs">
                    {{ $t('coop_download_selected_cvs') }}
                </b-button>
                <b-button variant="outline-success" @click="$refs.datatable.excel()">
                    {{ $t('coop_export_to_excel') }}
                </b-button>
            </div>

            <CommonModal ref="statusModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_update_status') }}
                </template>
                <template v-slot:CommonModalContent>
                    <div v-if="selectedApplication">
                        <b-form-group :label="$t('status')">
                            <b-form-select v-model="statusForm.status" :options="applicationStatusOptions" />
                        </b-form-group>
                        <b-form-group :label="$t('description')" v-if="statusForm.status === 'rejected'">
                            <b-form-textarea v-model="statusForm.description" rows="3"/>
                        </b-form-group>
                        <div v-if="statusForm.status === 'approved'">
                            <b-row>
                                <b-col cols="6">
                                    <b-form-group :label="$t('work_start_date')">
                                        <b-form-input v-model="statusForm.work_start_date" type="date"/>
                                    </b-form-group>
                                </b-col>
                                <b-col cols="6">
                                    <b-form-group :label="$t('work_end_date')">
                                        <b-form-input v-model="statusForm.work_end_date" type="date"/>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="small text-muted mb-2">{{ $t('coop_approval_email_note') }}</div>
                            <div class="small text-muted mb-2">{{ $t('coop_survey_note') }}</div>
                        </div>
                        <div class="d-flex justify-content-center mt-2">
                            <b-button variant="primary" @click="saveStatus">{{ $t('save') }}</b-button>
                        </div>
                    </div>
                </template>
            </CommonModal>

            <CommonModal ref="documentsModal" size="md" :onHideOnlyX="true">
                <template v-slot:CommonModalTitle>
                    {{ $t('coop_documents') }}
                </template>
                <template v-slot:CommonModalContent>
                    <div v-if="selectedApplication">
                        <div v-for="doc in documentsForm" :key="doc.code" class="d-flex align-items-center mb-2">
                            <div class="mr-2" style="width: 60px">{{ doc.label }}</div>
                            <b-button size="sm" variant="outline-secondary" class="mr-2" @click="downloadDocument(doc)">
                                <i class="ri-download-2-line"></i>
                            </b-button>
                            <b-form-select size="sm" v-model="doc.status" :options="documentStatusOptions" style="max-width: 160px"/>
                        </div>
                        <b-button variant="primary" class="mt-2" @click="saveDocumentStatus">{{ $t('save') }}</b-button>
                    </div>
                </template>
            </CommonModal>
        </app-layout>
    </div>
</template>
<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header'
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile'

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import FacultySelectbox from '@/components/interactive-fields/FacultySelectbox.vue';
    import DepartmentSelectbox from '@/components/interactive-fields/DepartmentSelectbox';
    import ClassesSelectbox from '@/components/interactive-fields/ClassesSelectbox';

    //Service
    import CoopAdService from '@/services/CoopAdService';
    import CoopAdApplicationService from '@/services/CoopAdApplicationService';

    //Other
    import qs from 'qs';

    export default {
        components: {
            AppLayout,
            Header,
            HeaderMobile,

            DatatableFilter,
            Datatable,
            CommonModal,
            FacultySelectbox,
            DepartmentSelectbox,
            ClassesSelectbox
        },
        data() {
            return {
                ad: null,
                selectedApplication: null,
                selectedRows: [],
                statusForm: {
                    status: null,
                    description: null,
                    work_start_date: null,
                    work_end_date: null
                },
                documentsForm: [],
                statusFilterOptions: [
                    {value: null, text: ''},
                    {value: 'in_review', text: this.$t('coop_status_in_review')},
                    {value: 'sent_to_company', text: this.$t('coop_status_sent_to_company')},
                    {value: 'company_evaluation', text: this.$t('coop_status_company_evaluation')},
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')},
                    {value: 'position_closed', text: this.$t('coop_status_position_closed')}
                ],
                applicationStatusOptions: [
                    {value: 'in_review', text: this.$t('coop_status_in_review')},
                    {value: 'sent_to_company', text: this.$t('coop_status_sent_to_company')},
                    {value: 'company_evaluation', text: this.$t('coop_status_company_evaluation')},
                    {value: 'approved', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')},
                    {value: 'position_closed', text: this.$t('coop_status_position_closed')}
                ],
                documentStatusOptions: [
                    {value: 'accepted', text: this.$t('approved')},
                    {value: 'rejected', text: this.$t('rejected')}
                ],
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_update_status'),
                                    class: 'ri-refresh-line',
                                    callback: (row) => {
                                        this.showStatusModal(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_delete_application'),
                                    class: 'ri-delete-bin-2-line',
                                    callback: (row) => {
                                        this.deleteApplication(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_download_cv'),
                                    class: 'ri-file-download-line',
                                    callback: (row) => {
                                        this.downloadCv(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_view_cv'),
                                    class: 'ri-eye-line',
                                    callback: (row) => {
                                        this.viewCv(row);
                                    }
                                },
                                {
                                    text: this.$t('coop_update_document_status'),
                                    class: 'ri-file-list-3-line',
                                    callback: (row) => {
                                        this.showDocumentsModal(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('department'),
                            field: 'department_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('gno'),
                            field: 'gno',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_cv'),
                            field: 'cv_file_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: 'status',
                            formatFn: (status) => {
                                const found = this.applicationStatusOptions.find((option) => option.value === status);
                                return found ? found.text : status;
                            },
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('class'),
                            field: 'class',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('telephone'),
                            field: 'telephone',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('email'),
                            field: 'email',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    filterStatus: true,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: 'id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            CoopAdService.get(this.$route.params.id).then((response) => {
                this.ad = response.data.data;
            });
            this.getRows();
        },
        methods: {
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                this.getRows();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            onSortChange(params) {
                const sortType = params[0].type == 'desc' ? '-' : '';
                this.datatable.queryParams.sort = sortType + params[0].field;
                this.getRows();
            },
            onSelectedRowsChange(params) {
                this.selectedRows = params.selectedRows;
            },
            getRows() {
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, {encode: false})
                };

                return CoopAdApplicationService.getAll(this.$route.params.id, config)
                                       .then((response) => {
                                           this.datatable.rows = response.data.data;
                                           this.datatable.total = response.data.pagination.total;
                                       })
                                       .finally(() => {
                                           this.datatable.isLoading = false;
                                       });
            },
            showStatusModal(row) {
                this.selectedApplication = row;
                this.statusForm = {
                    status: row.status,
                    description: row.rejection_reason,
                    work_start_date: row.work_start_date,
                    work_end_date: row.work_end_date
                };
                this.$refs.statusModal.$refs.commonModal.show();
            },
            saveStatus() {
                CoopAdApplicationService.updateStatus(this.selectedApplication.id, this.statusForm).then((response) => {
                    this.$toast.success(this.$t(response.data.message));
                    this.$refs.statusModal.$refs.commonModal.hide();
                    this.getRows();
                });
            },
            deleteApplication(row) {
                this.$swal.fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopAdApplicationService.remove(row.id).then((response) => {
                            this.$toast.success(this.$t(response.data.message));
                            this.getRows();
                        });
                    }
                });
            },
            downloadCv(row) {
                this.$toast.success(row.cv_file_name);
            },
            viewCv(row) {
                this.$toast.success(row.cv_file_name);
            },
            downloadSelectedCvs() {
                if (this.selectedRows.length === 0) {
                    return;
                }
                this.$toast.success(this.selectedRows.map((row) => row.cv_file_name).join(', '));
            },
            showDocumentsModal(row) {
                this.selectedApplication = row;
                this.documentsForm = row.documents.map((doc) => ({...doc}));
                this.$refs.documentsModal.$refs.commonModal.show();
            },
            downloadDocument(doc) {
                this.$toast.success(doc.label);
            },
            saveDocumentStatus() {
                CoopAdApplicationService.updateDocumentStatus(this.selectedApplication.id, this.documentsForm).then((response) => {
                    this.$toast.success(this.$t(response.data.message));
                    this.$refs.documentsModal.$refs.commonModal.hide();
                    this.getRows();
                });
            }
        }
    }
</script>
